import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Button } from '@/components/common';
import { FormattedNumberInput } from '@/utils/number-format';
import { QUANTITY_DECIMAL_PLACES } from '@/constants/number';
import { useUIStore } from '@/store/uiStore';
import { CreateCutConfigLineRequest } from '@/types/cutting';

interface CutConfigLineModalProps {
  configId?: string;
  onSave?: (line: CreateCutConfigLineRequest) => void;
}

const CutConfigLineModal: React.FC<CutConfigLineModalProps> = ({
  configId,
  onSave,
}) => {
  const { t } = useTranslation();
  const { modals, closeModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.cutConfigLine;

  const [formData, setFormData] = useState<CreateCutConfigLineRequest>({
    finalSize: '',
    quantity: 1,
    unit: 'tấm',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        finalSize: '',
        quantity: 1,
        unit: 'tấm',
        notes: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.finalSize.trim()) {
      newErrors.finalSize = 'Kích thước là bắt buộc';
    }
    if (formData.quantity <= 0) {
      newErrors.quantity = 'Số lượng phải lớn hơn 0';
    }
    if (!formData.unit.trim()) {
      newErrors.unit = 'Đơn vị là bắt buộc';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (onSave) {
        onSave(formData);
      }
      console.log('Cut config line saved successfully');
      handleClose();
    } catch (error) {
      console.error('Error saving cut config line:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('cutConfigLine');
    setFormData({
      finalSize: '',
      quantity: 1,
      unit: 'tấm',
      notes: '',
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof CreateCutConfigLineRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('tooltips.cutting.cutConfigLine')} // Title positioned top-left as specified
      size="md"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <div className="space-y-6">
        {/* Fields - vertical layout as specified */}
        
        {/* Config ID (readonly) */}
        <div>
          <label htmlFor="configId" className="block text-sm font-medium text-gray-700 mb-1">
            Config ID
          </label>
          <input
            type="text"
            id="configId"
            value={configId || 'Tự động gán'}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        {/* Size */}
        <div>
          <label htmlFor="finalSize" className="block text-sm font-medium text-gray-700 mb-1">
            Kích thước *
          </label>
          <input
            type="text"
            id="finalSize"
            value={formData.finalSize}
            onChange={(e) => handleInputChange('finalSize', e.target.value)}
            className={`input-field ${errors.finalSize ? 'border-red-500' : ''}`}
            placeholder="VD: 1200x800x18mm"
            disabled={loading}
          />
          {errors.finalSize && <p className="text-red-500 text-xs mt-1">{errors.finalSize}</p>}
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
            Số lượng *
          </label>
          <FormattedNumberInput
            id="quantity"
            value={formData.quantity}
            onChange={(value) => handleInputChange('quantity', Math.max(1, value))}
            className={`input-field ${errors.quantity ? 'border-red-500' : ''}`}
            maxDecimalPlaces={QUANTITY_DECIMAL_PLACES}
            disabled={loading}
          />
          {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>}
        </div>

        {/* Unit */}
        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
            Đơn vị *
          </label>
          <input
            type="text"
            id="unit"
            value={formData.unit}
            onChange={(e) => handleInputChange('unit', e.target.value)}
            className={`input-field ${errors.unit ? 'border-red-500' : ''}`}
            placeholder="VD: tấm, cái, m2..."
            disabled={loading}
          />
          {errors.unit && <p className="text-red-500 text-xs mt-1">{errors.unit}</p>}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Ghi chú
          </label>
          <textarea
            id="notes"
            rows={3}
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="input-field resize-none"
            placeholder="Ghi chú thêm cho dòng cắt..."
            disabled={loading}
          />
        </div>

        {/* Save Button - positioned bottom-right as specified */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Hủy
            </Button>
            
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
            >
              Lưu
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CutConfigLineModal;
